import { useState, useEffect } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { getPortfolio } from "@/lib/logos";
import { toast } from "sonner";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { motion } from "framer-motion";
import { getNetworkConfig } from "@/lib/api";
import { executeFbtcTrade, getBtcChart, getFbtcPrice, getFbtcQuote } from "@/lib/fbtcTrade";
import { tradeErrorMessage } from "@/lib/poolSwap";
import { FADE_UP_SLOW } from "@/lib/motion";
import { useWallet } from "@/contexts/WalletContext";
import { TokenLogo } from "@/components/common/TokenLogo";
import { TestTokenNote } from "@/components/common/TestTokenNote";
import { AmountBox, TokenPill, SubmitButton, TxLink, QuoteRow } from "@/components/exchange/TradeParts";
import { Card, CardContent } from "@/components/ui/card";
import { AXIS_TICK, TOOLTIP_STYLE, DOMAIN_DATA, DOMAIN_AUTO } from "@/lib/chartStyles";

const RANGES = ["24h", "7d", "30d"];
const usd = (n, d = 2) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })}`;
const fmtTime = (t, r) => new Date(t * 1000).toLocaleString(undefined, r === "24h" ? { hour: "2-digit", minute: "2-digit" } : { month: "short", day: "numeric" });

const RangeTabs = ({ range, setRange }) => (
  <div className="flex gap-1 rounded-full border border-[#2A2411] p-0.5">
    {RANGES.map((r) => (
      <button key={r} data-testid={`btc-chart-range-${r}`} onClick={() => setRange(r)}
        className={`px-2.5 py-1 text-xs rounded-full transition-colors ${range === r ? "bg-[#F7931A] text-black font-semibold" : "text-zinc-400 hover:text-white"}`}>{r.toUpperCase()}</button>
    ))}
  </div>
);

const BtcChart = () => {
  const [range, setRange] = useState("24h");
  const { data, isError } = useQuery({ queryKey: ["btcChart", range], queryFn: () => getBtcChart(range), refetchInterval: 60000 });
  const up = (data?.changePercent ?? 0) >= 0;
  return (
    <Card data-testid="btc-chart" className="border-[#2A2411] bg-[#0A0A0A]">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <p className="text-xs text-zinc-500 flex items-center gap-2"><TokenLogo symbol="FBTC" size="xs" /> BTC / USD · {data?.source || "CoinGecko"}</p>
            <p data-testid="btc-chart-current" className="font-display text-3xl font-bold text-white mt-1">{data ? usd(data.current) : "—"}</p>
            {data && <p data-testid="btc-chart-change" className={`text-xs font-mono ${up ? "text-green-400" : "text-red-400"}`}>{up ? "+" : ""}{data.changePercent}% · H {usd(data.high, 0)} · L {usd(data.low, 0)}</p>}
          </div>
          <RangeTabs range={range} setRange={setRange} />
        </div>
        <div className="h-64">
          {isError && <p data-testid="btc-chart-error" className="text-sm text-zinc-500 pt-24 text-center">BTC chart temporarily unavailable</p>}
          {data && (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.points}>
                <defs><linearGradient id="btcFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F7931A" stopOpacity={0.35} /><stop offset="100%" stopColor="#F7931A" stopOpacity={0} /></linearGradient></defs>
                <XAxis dataKey="t" type="number" domain={DOMAIN_DATA} tickFormatter={(t) => fmtTime(t, range)} tick={AXIS_TICK} axisLine={false} tickLine={false} minTickGap={40} />
                <YAxis domain={DOMAIN_AUTO} hide />
                <Tooltip labelFormatter={(t) => fmtTime(t, range)} formatter={(v) => [usd(v), "BTC"]} contentStyle={TOOLTIP_STYLE} />
                <Area type="monotone" dataKey="price" stroke="#F7931A" strokeWidth={2} fill="url(#btcFill)" isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const useFbtcQuote = (side, amount) => {
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    setQuote(null);
    setError(null);
    if (!amount || Number(amount) <= 0) return undefined;
    const t = setTimeout(() => getFbtcQuote(side, Number(amount)).then(setQuote).catch((e) => setError(e?.response?.data?.detail || "Could not fetch quote")), 400);
    return () => clearTimeout(t);
  }, [side, amount]);
  return { quote, error, clear: () => setQuote(null) };
};

const TradeCard = () => {
  const [side, setSide] = useState("buy");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("");
  const [lastTx, setLastTx] = useState(null);
  const { address, getProvider, refreshBalances } = useWallet();
  const { data: config } = useQuery({ queryKey: ["networkConfig"], queryFn: getNetworkConfig });
  const { data: price } = useQuery({ queryKey: ["fbtcPrice"], queryFn: getFbtcPrice, refetchInterval: 15000 });
  const { quote, error, clear } = useFbtcQuote(side, amount);
  const [payToken, getToken] = side === "buy" ? ["USDT", "FBTC"] : ["FBTC", "USDT"];
  const { data: portfolio } = useQuery({ queryKey: ["portfolio", address?.toLowerCase()], queryFn: () => getPortfolio(address),
    enabled: Boolean(address), refetchInterval: 6000, placeholderData: keepPreviousData });
  const payBalance = portfolio?.assets.find((a) => a.symbol === payToken)?.balance ?? 0;
  const decimals = payToken === "USDT" ? 6 : 8;
  const maxExtra = address && (
    <div className="flex items-center gap-2 text-xs">
      <span data-testid="fbtc-pay-balance" className="text-zinc-500">Balance: {Number(payBalance).toLocaleString(undefined, { maximumFractionDigits: decimals })} {payToken}</span>
      <button data-testid="fbtc-max-btn" onClick={() => setAmount(String(Math.floor(payBalance * 10 ** decimals) / 10 ** decimals))}
        className="px-2 py-0.5 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] font-semibold hover:bg-[#D4AF37]/10 transition-colors">MAX</button>
    </div>
  );

  const execute = async () => {
    if (!address) return toast.error("Connect your wallet first");
    setBusy(true);
    try {
      const tokenIn = side === "buy" ? config.usdt.address : config.fbtc.address;
      const hash = await executeFbtcTrade({ eip1193: getProvider(), quote, tokenIn, onStep: setStep });
      toast.success(`${side === "buy" ? "Bought" : "Sold"} FBTC: received ${quote.output} ${quote.outputToken}`);
      setLastTx(hash);
      setAmount("");
      clear();
      setTimeout(() => refreshBalances(), 1500);
    } catch (err) {
      toast.error(tradeErrorMessage(err, side === "buy" ? "Purchase" : "Sale", "gas"));
    } finally {
      setBusy(false);
      setStep("");
    }
  };

  return (
    <Card data-testid="fbtc-trade" className="border-[#2A2411] bg-[#0A0A0A]/80 gold-glow">
      <CardContent className="p-5 space-y-4">
        <div className="grid grid-cols-2 gap-1 p-1 rounded-full border border-[#2A2411]">
          {["buy", "sell"].map((s) => (
            <button key={s} data-testid={`fbtc-side-${s}`} onClick={() => { setSide(s); setAmount(""); setLastTx(null); }}
              className={`py-2 rounded-full text-sm font-semibold transition-colors ${side === s ? "bg-[#D4AF37] text-black" : "text-zinc-400 hover:text-white"}`}>
              {s === "buy" ? "Buy FBTC" : "Sell FBTC"}
            </button>
          ))}
        </div>
        <AmountBox label={`You Pay (${payToken})`} amount={amount} onChange={setAmount} extra={maxExtra}><TokenPill symbol={payToken} /></AmountBox>
        {error && <p data-testid="fbtc-quote-error" className="text-sm text-red-400">{error}</p>}
        {quote && (
          <div data-testid="fbtc-quote" className="p-4 rounded-xl bg-[#050505] border border-[#2A2411] space-y-2">
            <QuoteRow label="Rate (live BTC)" value={`1 FBTC = ${usd(quote.price)}`} testId="fbtc-quote-rate" />
            <QuoteRow label={`Fee (${quote.feePercent}%)`} value={`${quote.fee} USDT`} testId="fbtc-quote-fee" />
            <QuoteRow strong label="You Receive" value={`${quote.output} ${getToken}`} testId="fbtc-quote-output" valueClass="text-[#D4AF37] font-display font-bold" />
          </div>
        )}
        {price && !price.tradable && <p data-testid="fbtc-not-tradable" className="text-xs text-yellow-400">Price feed is updating; trading resumes in a moment.</p>}
        <SubmitButton testId="fbtc-submit" onClick={execute} disabled={!quote || busy} executing={busy} step={step} address={address} amount={amount}
          connectLabel="Connect Wallet to Trade FBTC">{side === "buy" ? "Buy FBTC" : "Sell FBTC"}</SubmitButton>
        <TxLink hash={lastTx} testId="fbtc-tx-link" label="View FBTC transaction" />
      </CardContent>
    </Card>
  );
};

export default function FbtcPage() {
  return (
    <div data-testid="fbtc-page" className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <motion.div {...FADE_UP_SLOW} className="mb-8 flex items-center gap-4">
        <TokenLogo symbol="FBTC" size="lg" />
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight"><span className="text-[#F7931A]">FBTC</span> · FSC Bitcoin</h1>
          <p className="text-zinc-400 mt-1">Test token on FMI Smart Chain priced at the live BTC/USD rate. FBTC is not BTC-backed.</p>
          <TestTokenNote testId="fbtc-test-token-note" className="mt-2" />
        </div>
      </motion.div>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3"><BtcChart /></div>
        <div className="lg:col-span-2"><TradeCard /></div>
      </div>
    </div>
  );
}
